import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  UserCheck,
  GraduationCap,
  Shield,
  TrendingUp,
  Activity,
  Clock,
  CheckCircle
} from 'lucide-react';
import { userService } from '../../services/database/userService';
import { User } from '../../types/auth';
import { toast } from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';

interface DashboardStats {
  totalUsers: number;
  totalStudents: number;
  totalTeachers: number;
  totalAdmins: number;
  pendingApprovals: number;
  activeToday: number;
}

/**
 * Admin Dashboard Page
 * Trang tổng quan cho admin với thống kê và quick actions
 */
const AdminDashboard: React.FC = () => {
  const { user: currentUser, isLoading: authLoading, isAuthenticated } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalStudents: 0,
    totalTeachers: 0,
    totalAdmins: 0,
    pendingApprovals: 0,
    activeToday: 0,
  });
  const [recentUsers, setRecentUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  // ✅ Load dashboard data KHI auth loading xong VÀ user đã authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated && currentUser) {
      console.log('📊 [AdminDashboard] Auth loaded, loading dashboard for admin:', currentUser.email);
      loadDashboardData();
    } else if (!authLoading && !isAuthenticated) {
      console.log('❌ [AdminDashboard] Not authenticated after loading');
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, currentUser]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Get system stats from backend (optimized)
      const dashboardStats = await userService.getSystemStats();

      setStats(dashboardStats);

      // Get 5 most recent users (still need to fetch list but small limit)
      // Pass { limit: 5 } to getAllUsers to optimize
      const { users: recent } = await userService.getAllUsers({ limit: 5 });
      setRecentUsers(recent);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Tổng người dùng',
      value: stats.totalUsers,
      icon: <Users className="w-8 h-8" />,
      color: 'bg-blue-500',
      link: '/admin/users',
    },
    {
      title: 'Học sinh',
      value: stats.totalStudents,
      icon: <GraduationCap className="w-8 h-8" />,
      color: 'bg-green-500',
      link: '/admin/users?role=student',
    },
    {
      title: 'Giáo viên',
      value: stats.totalTeachers,
      icon: <UserCheck className="w-8 h-8" />,
      color: 'bg-purple-500',
      link: '/admin/users?role=teacher',
    },
    {
      title: 'Quản trị viên',
      value: stats.totalAdmins,
      icon: <Shield className="w-8 h-8" />,
      color: 'bg-red-500',
      link: '/admin/users?role=admin',
    },
    {
      title: 'Chờ duyệt',
      value: stats.pendingApprovals,
      icon: <Clock className="w-8 h-8" />,
      color: 'bg-yellow-500',
      link: '/admin/teachers',
    },
    {
      title: 'Hoạt động hôm nay',
      value: stats.activeToday,
      icon: <Activity className="w-8 h-8" />,
      color: 'bg-indigo-500',
      link: '/admin/users?active=today',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Tổng quan hệ thống và thống kê</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <Link
            key={index}
            to={card.link}
            className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">{card.title}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{card.value}</p>
              </div>
              <div className={`${card.color} text-white p-3 rounded-lg`}>
                {card.icon}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Người dùng mới</h2>
            <Link
              to="/admin/users"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Xem tất cả →
            </Link>
          </div>
          <div className="space-y-3">
            {recentUsers.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">Chưa có người dùng nào</p>
            ) : (
              recentUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition"
                >
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-medium">{user.name[0]}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  <span className={`
                    px-2 py-1 text-xs font-medium rounded
                    ${user.role === 'admin' ? 'bg-red-100 text-red-700' : ''}
                    ${user.role === 'teacher' ? 'bg-purple-100 text-purple-700' : ''}
                    ${user.role === 'student' ? 'bg-green-100 text-green-700' : ''}
                  `}>
                    {user.role === 'admin' && 'Admin'}
                    {user.role === 'teacher' && 'Giáo viên'}
                    {user.role === 'student' && 'Học sinh'}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Thao tác nhanh</h2>
          <div className="space-y-3">
            <Link
              to="/admin/users"
              className="flex items-center space-x-3 p-3 hover:bg-blue-50 rounded-lg transition border border-transparent hover:border-blue-200"
            >
              <Users className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <p className="font-medium text-gray-900">Quản lý người dùng</p>
                <p className="text-xs text-gray-500">Xem và chỉnh sửa thông tin người dùng</p>
              </div>
            </Link>

            <Link
              to="/admin/roles"
              className="flex items-center space-x-3 p-3 hover:bg-purple-50 rounded-lg transition border border-transparent hover:border-purple-200"
            >
              <Shield className="w-5 h-5 text-purple-600" />
              <div className="flex-1">
                <p className="font-medium text-gray-900">Phân quyền</p>
                <p className="text-xs text-gray-500">Cấu hình roles và permissions</p>
              </div>
            </Link>

            {stats.pendingApprovals > 0 && (
              <Link
                to="/admin/teachers"
                className="flex items-center space-x-3 p-3 hover:bg-yellow-50 rounded-lg transition border border-yellow-200"
              >
                <Clock className="w-5 h-5 text-yellow-600" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Duyệt giáo viên</p>
                  <p className="text-xs text-gray-500">
                    {stats.pendingApprovals} giáo viên chờ duyệt
                  </p>
                </div>
                <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-bold">
                  {stats.pendingApprovals}
                </span>
              </Link>
            )}

            <Link
              to="/admin/settings"
              className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition border border-transparent hover:border-gray-200"
            >
              <CheckCircle className="w-5 h-5 text-gray-600" />
              <div className="flex-1">
                <p className="font-medium text-gray-900">Cài đặt hệ thống</p>
                <p className="text-xs text-gray-500">Cấu hình chung cho hệ thống</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

